import React, { useEffect, useMemo, useCallback } from "react";
import { Grid, Typography, Icon, Button, styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

// Components
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";

// Redux
import {
  IQueryArguments,
  IDynamicQuery,
  IContent,
  IContentType,
  IUser,
  IFilterOption,
  IFlightAircraft,
  ISavedFilter,
} from "@vmosolutions/oc-interfaces";
import _ from "lodash";

// Styling
import classNames from "classnames";

import { FilterWithDateInput, returnSelectedOptions } from "./Helpers";

import { convertFilter2Query, reducedFilter, filterContent } from "./ContentHelper";
import { searchContent, getContentTypes } from "src/redux/actions/ContentManagement/ContentManagement.Actions";
import { ISearchStatus, defaultSearchStatus } from "./Helpers";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { storeAppUsers } from "src/redux/actions/Structure.Actions";

// Review status filter
import { ReviewStatusFilter } from "src/pages/ContentManagement/FilterFields/ReviewStatusFilter";
import { IStructuresState } from "src/redux/reducers/StructuresReducer";
import { FlightDropTargetFilter } from "src/pages/ContentManagement/FlightDropTargetFilter";

const OCContentEventFilter = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  "&.saved_search_selection": {
    marginLeft: 10,
    width: "auto",
    "& .saved_search_icon": {
      position: "relative",
      width: 24,
      height: 24,
      "& .search_icon": {},
      "& .star_icon": {
        width: 9,
        height: 10,
        top: 4,
        left: 5,
        position: "absolute",
      },
    },
    "& .timeline_event_saved_search_filter": {
      width: 263,
    },
  },
  "& .filter_drop_down_menu": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    margin: "5px 10px",
    alignItems: "flex-start",
    "& .timeline_event_search_filter": {
      width: 263,
    },
  },
  "& .active_filters": {
    "& .filter": { width: 263 },
    display: "flex",
    marginLeft: 10,
    alignItems: "flex-start",
    flexWrap: "wrap",
    "& .date_range_filter": {
      display: "flex",
      width: 263,
      flexDirection: "column",
      margin: 5,
      "& .from_date, & .to_date": {
        margin: 0,
        "& label": { width: 68 },
      },
      "& .daysofweek_selection": {
        margin: 5,
        "& .input_control label": { width: 68, margin: "0px 10px auto auto" },
        "& .react-select-container": { width: 175 },
      },
    },
    "& button.search_button": {
      margin: 3,
      padding: "2px 8px",
      fontSize: 12,
      fontWeight: 300,
      width: 100,
      "& .search_icon": { fontSize: 18, marginRight: 5 },
    },
    "& .results_match_filters": {
      margin: 3,
      borderRadius: 3,
      width: 26,
      height: 26,
      border: `1px solid ${theme.palette.success.main}`,
      "& path": { fill: theme.palette.success.main },
    },
  },
}));

export const contentFilterOptions: IFilterOption[] = [
  {
    value: "c_contentType",
    label: "Content Type",
    type: "contentEvent",
    searchValue: null,
  },
  {
    value: "c_associatedFlights",
    label: "Associated Flights",
    type: "contentEvent",
    searchValue: null,
  },
  {
    value: "c_reviewStatus",
    label: "Review Status",
    type: "contentEvent",
    searchValue: null,
  },
  {
    value: "c_fileName",
    label: "File Name",
    type: "fileName",
    searchValue: null,
  },
  {
    value: "c_aircraft",
    label: "Associated Flight's Aircraft",
    type: "contentEvent",
    searchValue: null,
  },
  {
    value: "c_fileType",
    label: "File Type",
    type: "contentEvent",
    searchValue: null,
  },
  {
    value: "c_uploadedBy",
    label: "Uploaded By",
    type: "contentEvent",
    searchValue: null,
  },
  {
    value: "c_modifiedAt",
    label: "Modified At",
    type: "contentEvent",
    searchValue: null,
  },
  {
    value: "c_createdAt",
    label: "Created At",
    type: "contentEvent",
    searchValue: null,
  },
  {
    value: "c_source",
    label: "Source",
    type: "contentEvent",
    searchValue: null,
  },
];

interface IEventFilter {
  dataType?: string;
  dataInput?: any; // incoming event dataset
  dataOutput: (data: any) => any; // returns filtered dataset
  allowMultiFilter?: boolean;
  parentElement?: any;
  hideLabel?: boolean;
  searchStatus?: (status: ISearchStatus) => void;
  noFilter?: boolean;
  showDateRangeRRule?: boolean;
  setSearchFilters?: React.Dispatch<React.SetStateAction<IFilterOption[]>>;
  applySavedFilter?: ISavedFilter;
  //mode?: 'filter'|'search'
}

// If search query changes, determine if loaded events support filtering...
// ...or if they should be cleared and searched again.
const shouldFilterResults = (loadedQuery: IDynamicQuery, newFilters: any): boolean => {
  let shouldFilter = true;
  const newQuery: IDynamicQuery = convertFilter2Query(newFilters);
  loadedQuery?.queryArgs?.forEach((args: IQueryArguments, index: number) => {
    if (newQuery?.queryArgs?.length > 0 && newQuery.queryArgs[index].value) {
      shouldFilter = _.isEqual(args, newQuery.queryArgs[index]);
    }
  });
  //console.log("shouldFilter", shouldFilter);
  return shouldFilter;
};

/**
 * EventFilter component handles the ability to search content
 * @param props -
 */
export const ContentEventFilter = (props: IEventFilter): JSX.Element => {
  const dispatch = useDispatch();
  const {
    // dataType,
    dataOutput,
    parentElement,
    hideLabel,
    allowMultiFilter,
    searchStatus,
    noFilter,
    showDateRangeRRule,
    setSearchFilters,
    applySavedFilter,
  } = props;

  // Store Events loaded by Database Search
  const [databaseResults, setDatabaseResults] = React.useState<IContent[]>([]);
  const [searching, setSearching] = React.useState<boolean>(false);

  // Store filtered events
  const [filteredEvents, setFilteredEvents] = React.useState<IContent[]>([]);
  const [selectedFilter, setSelectedFilter] = React.useState<IFilterOption[]>([]);
  const [searchedQuery, setSearchedQuery] = React.useState<IDynamicQuery>(null);
  const [contentTypes, setContentTypes] = React.useState<IContentType[]>(null);

  // Load appUsers from redux, fetch them if not available.
  const currentUser: string = useSelector((state: IRootState) => state?.authentication?.attributes._id);
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { appUsers, customerStructure } = structures;
  const { aircraft } = customerStructure;

  // Store search and selected filters, compared to verify results match current filters.
  const [searchFilter, setSearchFilter] = React.useState<IFilterOption[]>([]);
  const filterType = selectedFilter?.length > 0 ? selectedFilter[0]?.type : undefined;
  const dataType = useMemo(() => props?.dataType ?? filterType, [props?.dataType, filterType]);

  // If a selected search filter is passed in from saved filters, apply it. - CC
  useEffect(() => {
    if (applySavedFilter?.dataType === dataType) {
      setSelectedFilter(applySavedFilter?.filters ?? []);
    }
  }, [applySavedFilter, dataType]);

  // If there are valid filters present, update the upstream search filters, otherwise, clear them
  useEffect(() => {
    if (setSearchFilters) setSearchFilters(selectedFilter ?? []);
  }, [selectedFilter, setSearchFilters]);

  const resultsMatchFilter = useMemo(() => {
    return _.isEqual(searchFilter, selectedFilter);
  }, [searchFilter, selectedFilter]);

  // const appUsers = useSelector((state: IRootState) => state.structures.appUsers);
  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users");
      if (response) dispatch(storeAppUsers(response));
    };
    if (appUsers?.length === 0) getUsersAction();
  }, [dispatch, appUsers]);

  // Make sure all filters contain a value before enabling the search button.
  const enableSearch = useMemo(() => {
    let searchValid = true;
    selectedFilter?.forEach((filter) => {
      if (!filter.searchValue || (Array.isArray(filter.searchValue) && filter.searchValue?.length === 0)) {
        searchValid = false; // set search as invalid if any filters have no assigned value.
      }
    });
    return searchValid;
  }, [selectedFilter]);

  const eventType = useMemo(() => {
    return "Content";
  }, []);

  useEffect(() => {
    const getTypes = async (): Promise<void> => {
      const types = await getContentTypes();
      setContentTypes(types);
    };
    getTypes();
  }, []);

  // Clear all filters and results. (resets the tool)
  const handleClearAll = useCallback(() => {
    setSearchFilter([]);
    setSelectedFilter([]);
    setFilteredEvents([]);
  }, []);

  // If data type changes externally , reset tool.
  useEffect(() => {
    handleClearAll();
  }, [dataType, handleClearAll]);

  // Debounced search call.
  // This reduces the possibility of making duplicate APIs calls in quick succession.
  const debouncedSearchContent = _.debounce(async (selectedFilter: any): Promise<void> => {
    const searchPayload: IDynamicQuery = convertFilter2Query(selectedFilter);
    // Only make the api call if there are filters showing in the query.
    if (searchPayload?.queryArgs?.length > 0) {
      searchStatus({
        active: true,
        message: `Searching for requested ${eventType}...`,
      });
      setSearching(true);
      const searchResults = await searchContent(searchPayload);

      // If response isn't an array, empty table so that search status can be shown.
      const newResults = Array.isArray(searchResults) ? (searchResults as any[]) : [];
      setDatabaseResults(newResults);
      //Store current selected filter (clone to ensure no mutation) as search filter.
      setSearchFilter(_.cloneDeep(selectedFilter));
      setSearchedQuery(searchPayload);
      setSearching(false);

      if (Array.isArray(searchResults)) {
        if (searchResults.length > 0) {
          searchStatus(defaultSearchStatus);
        } else {
          searchStatus({
            active: false,
            message: `No ${eventType} found, please change filter and try again.`,
          });
        }
      } else {
        const error: { status?: number; statusText?: string } = searchResults as any;
        //console.log("search error:", error);
        switch (error?.status) {
          case 413: // Response exceeds allowable size
            searchStatus({
              active: false,
              code: 413,
              message: "Response size too large, please narrow search parameters and try again.",
            });
            break;
          default:
            searchStatus({
              active: false,
              message: "Something went wrong please try again or contact administrator for assistance.",
            });
          // code block
        }
      }
    }
  }, 1000);

  // If databaseResults are loaded, store them as first filtered events.
  useEffect(() => {
    setFilteredEvents(databaseResults);
  }, [databaseResults]);

  // If filtered events change, update parent.
  useEffect(() => {
    if (dataOutput) dataOutput(filteredEvents);
  }, [filteredEvents, dataOutput]);

  const contentFilters = useMemo(() => {
    return contentTypes?.find((type: IContentType) => type?.type === "JourneyLogSheet")
      ? contentFilterOptions.concat({
          value: "c_logSheetNumber",
          label: "Log Sheet Number",
          type: "contentEvent",
          searchValue: null,
        })
      : contentFilterOptions;
  }, [contentTypes]);
  const selectionGroupedOptions = useMemo(() => {
    const groupArray = [];
    groupArray.push({
      label: "Content Fields",
      options: contentFilters,
    });
    return groupArray;
  }, [contentFilters]);

  /**
   * Accepts the user's input & determines the type of the user's input.
   * @param event -
   */
  const handleSearch = (key: string, event: any): void => {
    const newFilter = [...selectedFilter];
    const searchValue = !!event && !!event.target ? event.target.value : event;
    const filter = newFilter.find((filter: IFilterOption) => filter.value.includes(key));
    switch (key) {
      case "modifiedAt":
      case "createdAt": {
        const minMaxfilter = newFilter.find((filter: IFilterOption) => filter.value.includes(key));
        if (minMaxfilter) {
          minMaxfilter.searchValue = event;
        }
        break;
      }
      // All fields which use _id to define selected values.
      case "aircraft":
        if (filter) {
          filter.searchValue = searchValue?.map((option: any) => option._id);
        }
        break;
      case "logSheetNumber": {
        // Store logsheet as a number for the search.
        const numberArray = searchValue
          ? searchValue.split(",").map((logSheetNumber: string) => Number(logSheetNumber.trim()))
          : null;
        filter.searchValue = numberArray;
        break;
      }
      default:
        if (filter) {
          filter.searchValue = searchValue;
        }
    }
    searchStatus(defaultSearchStatus);
    setSelectedFilter(newFilter);
  };

  /**
   * Handles the user's selected filter to search for a flight.
   * @param selectedFilter -
   */
  const handleSelectedFilter = (newFilters: any): void => {
    const filterArray = !newFilters
      ? []
      : Array.isArray(newFilters)
        ? _.cloneDeep(newFilters)
        : [_.cloneDeep(newFilters)];
    // Carry over any currently selected filter values (required for using multiple filters)
    if (filterArray.length > 0) {
      filterArray.map((newF: IFilterOption) => {
        const filter = selectedFilter.find((oldF: IFilterOption) => oldF.value === newF.value);
        if (!!filter && !!filter.searchValue) {
          newF.searchValue = filter.searchValue;
        }
        return false;
      });
    }
    searchStatus(defaultSearchStatus);
    setSelectedFilter(filterArray);
  };

  const reducedFilters = useMemo(() => {
    if (
      !noFilter ||
      (selectedFilter?.length > 0 && !searchedQuery) ||
      (selectedFilter?.length > 0 &&
        searchedQuery?.queryArgs?.length > 0 &&
        shouldFilterResults(searchedQuery, selectedFilter))
    ) {
      const filtersObject = reducedFilter(selectedFilter, allowMultiFilter);
      return Object.keys(filtersObject).length > 0 ? filtersObject : null;
    }
    return null;
  }, [selectedFilter, noFilter, allowMultiFilter, searchedQuery]);

  useEffect(() => {
    // filter the results using the last retrieved database values.
    const newFilteredEvents = filterContent(reducedFilters, databaseResults);
    // If changing the filter has modified the resulting events, update the filtered events array and update parent.
    if (filteredEvents.length && !_.isEqual(filteredEvents, newFilteredEvents)) {
      setFilteredEvents(newFilteredEvents);
    }
  }, [reducedFilters, databaseResults, filteredEvents, dataOutput]);

  /**
   * Maps out selected filter array and returns the selected field inputs if selected.
   */
  const renderFilterInputs = (): JSX.Element[] => {
    if (!selectedFilter || selectedFilter.length === 0) {
      return null;
    }

    return selectedFilter.map((filter: IFilterOption, index: number) => {
      const { label, value } = filter;
      const dateTypes = ["modifiedAt", "createdAt"];
      const key = value.replace("c_", "");

      // Date Fields
      if (dateTypes.includes(key)) {
        return (
          <div key={index}>
            <FilterWithDateInput
              className="filter"
              dateTimeRange={filter.searchValue}
              input={{ type: "text" }}
              name={label}
              onChange={(event: any) => handleSearch(key, event)}
              //setDateInput={updateSpecificSearchFilter(key)}
              type="date"
              showDateRangeRRule={showDateRangeRRule}
            />
          </div>
        );
      } else if (key === "associatedFlights") {
        return (
          <Grid key={index} item sx={{ marginRight: "15px", marginBottom: "15px" }}>
            <FlightDropTargetFilter value={filter.searchValue} onChange={(event: any) => handleSearch(key, event)} />
          </Grid>
        );
      } else if (key === "fileName" || key === "fileType") {
        return (
          <div key={index}>
            <TextField
              className="auto_width filter table_filter_textfield"
              label={label}
              name={key}
              value={filter.searchValue}
              onChange={(event: any) => handleSearch(key, event)}
              // placeholder=""
              // multiline={true}
              // minRows={2}
              // maxRows={4}
            />
          </div>
        );
      } else if (key === "source" || key === "logSheetNumber") {
        return (
          <div key={index}>
            <TextField
              className="auto_width filter table_filter_textfield"
              label={label}
              name={key}
              value={filter.searchValue}
              onChange={(event: any) => handleSearch(key, event)}
              // placeholder=""
              // multiline={true}
              // minRows={2}
              // maxRows={4}
            />
          </div>
        );
      } else if (key === "aircraft") {
        return (
          <div key={index}>
            <SelectField
              className={"auto_width filter flight_aircraft_search"}
              label="Aircraft"
              name={key}
              value={returnSelectedOptions(aircraft, filter.searchValue, "_id")}
              isMulti={true}
              onChange={(aircraft: IFlightAircraft[]) => handleSearch(key, aircraft)}
              options={aircraft}
              getOptionLabel={(aircraft: IFlightAircraft) =>
                aircraft
                  ? `${aircraft?.registration} ${
                      aircraft?.aircraftType?.title && "(" + aircraft.aircraftType.title + ")"
                    }`
                  : ""
              }
              getOptionValue={(aircraft: IFlightAircraft) => aircraft._id}
              noOptionsMessage={() => "no aircraft available"}
              isSearchable={true}
              isClearable={true}
              menuPosition="fixed"
              maxMenuHeight={400}
              menuPortalTarget={parentElement}
              openMenuOnFocus={false}
              blurInputOnSelect={true}
              closeMenuOnSelect={true}
              placeholder=""
            />
          </div>
        );
      } else if (key === "contentType") {
        return (
          <div key={index}>
            <SelectField
              className="auto_width filter"
              label={label}
              name={key}
              value={filter.searchValue}
              isMulti={true}
              onChange={(event: any) => handleSearch(key, event)}
              options={contentTypes?.sort((a, b) => {
                return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
              })}
              getOptionLabel={(option: any) => (option ? option.title : "")}
              getOptionValue={(option: any) => option._id}
              noOptionsMessage={() => "no content options available"}
              isSearchable={true}
              isClearable={true}
              menuPosition="fixed"
              maxMenuHeight={400}
              menuPortalTarget={parentElement}
              openMenuOnFocus={false}
              blurInputOnSelect={true}
              closeMenuOnSelect={true}
              placeholder=""
            />
          </div>
        );
      } else if (key === "uploadedBy") {
        return (
          <div key={index}>
            <SelectField
              className="auto_width filter"
              label={label}
              name={key}
              value={filter.searchValue}
              isMulti={true}
              onChange={(users: IUser[]) => handleSearch(key, users)}
              options={appUsers.sort((a, b) => {
                return a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
              })}
              getOptionLabel={(user: IUser) => {
                const isMe = currentUser === user._id;
                return `${user.lastName}, ${user.firstName} ${user?.employeeId ? `(${user.employeeId})` : ""} ${
                  isMe ? "(Me)" : ""
                }`;
              }}
              getOptionValue={(user: IUser) => user._id}
              noOptionsMessage={() => "no user options available"}
              isSearchable={true}
              isClearable={true}
              menuPosition="fixed"
              maxMenuHeight={400}
              menuPortalTarget={parentElement}
              openMenuOnFocus={false}
              blurInputOnSelect={true}
              closeMenuOnSelect={true}
              placeholder=""
            />
          </div>
        );
      } else if (key === "reviewStatus") {
        return (
          <div key={index}>
            <ReviewStatusFilter value={filter.searchValue} onChange={(value) => handleSearch(key, value)} />
          </div>
        );
      } else {
        return null;
      }
    });
  };

  return (
    <OCContentEventFilter className={classNames("search_filters")}>
      {!hideLabel && (
        <Typography variant="subtitle1" className="body_text">
          Find Content by:
        </Typography>
      )}

      <div className="auto_width filter_drop_down_menu">
        {hideLabel && <Icon className="control_icon">filter_list</Icon>}
        <SelectField
          className="timeline_event_search_filter"
          label=""
          name="advanced_search_filters"
          value={selectedFilter}
          options={selectionGroupedOptions}
          onChange={handleSelectedFilter}
          isMulti={allowMultiFilter}
          isClearable={true}
          menuPortalTarget={parentElement}
          menuPosition="fixed"
          maxMenuHeight={400}
          multiMode={allowMultiFilter ? "checkbox" : null}
          placeholder={
            selectedFilter?.length === 0
              ? "Select Filters..."
              : selectedFilter?.length === 1
                ? selectedFilter[0].label
                : "Multiple Filters Selected..."
          }
        />
      </div>

      {selectedFilter?.length > 0 && (
        <div className="active_filters">
          {renderFilterInputs()}
          <Button
            id={"search_btn"}
            variant="outlined"
            onClick={() => debouncedSearchContent(selectedFilter)}
            disabled={!enableSearch || searching}
            className="search_button"
          >
            {!searching ? (
              <>
                <Icon className="search_icon">{resultsMatchFilter ? "refresh" : "search"}</Icon>
                {resultsMatchFilter ? "Refresh" : "Search"}
              </>
            ) : (
              <>
                <CircularProgress color="inherit" style={{ width: 14, height: 14, marginRight: 8 }} /> searching
              </>
            )}
          </Button>
        </div>
      )}
    </OCContentEventFilter>
  );
};
